import { Button, Skeleton, useStatusOverlay } from '@fs/zion-ui'
import ZionForm, { type FieldErrors, FormMultilineTextField, FormTextField, useZionForm } from '@fs/zion-form'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { isAxiosError } from 'axios'
import { NoticeLoading } from '@fs/zion-icon'
import { useGroupsContext } from '@fs/zion-tree-groups'
import { useTreeGroupDetailsContext } from '../providers/TreeGroupDetailsProvider'
import { editGroup } from '../../services/editGroup'

const MAX_NAME_CHARS = 100
const MAX_DESCRIPTION_CHARS = 1000

type FormValues = {
  name: string
  description: string
}

export default function EditTreeDetails(): JSX.Element {
  const [t] = useTranslation()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const showStatusOverlay = useStatusOverlay()
  const { group, refetchGroup } = useTreeGroupDetailsContext()
  const { getAndSetGroups } = useGroupsContext()
  const { name: groupName, description: groupDescription } = group
  const methods = useZionForm<FormValues>({
    mode: 'all',
    defaultValues: {
      name: groupName,
      description: groupDescription,
    },
    resolver: (values) => {
      const errors: FieldErrors<FormValues> = {}

      const errorMessage = t('group-management.illegal-input-character')
      if (!values.name) errors.name = { type: 'required', message: t('group-management.required.label') }
      if (values.name && /[`~$^+=<>|]/.test(values.name)) errors.name = { type: 'validate', message: errorMessage }
      if (values.description && /[`~$^+=<>|]/.test(values.description))
        errors.description = { type: 'validate', message: errorMessage }

      // Return empty values object when there are errors, per react-hook-form resolver contract
      if (Object.keys(errors).length > 0) {
        return { values: {}, errors }
      }

      return { values, errors: {} }
    },
  })
  const {
    reset,
    formState: { isDirty },
  } = methods

  useEffect(() => {
    reset({
      name: groupName,
      description: groupDescription,
    })
  }, [groupName, groupDescription, reset])

  const onSubmit = (values: FormValues): void => {
    setIsSubmitting(true)
    editGroup({ currentGroup: group, name: values.name, description: values.description })
      .then(() => {
        refetchGroup()
        getAndSetGroups?.(true)
        showStatusOverlay({
          message: t('group-management.save-success'),
          type: 'success',
          dismissable: true,
        })
        return undefined
      })
      .catch((error: unknown) => {
        console.error(error)

        if (isAxiosError(error) && error.response?.headers?.warning) {
          const warningHeader = error.response.headers.warning
          if (warningHeader.includes('40310')) {
            // User banished
            showStatusOverlay({ message: t('group-management.error.user-banished'), type: 'error', dismissable: true })
          } else if (warningHeader.includes('40311')) {
            // User must be member
            showStatusOverlay({
              message: t('group-management.error.user-not-member'),
              type: 'error',
              dismissable: true,
            })
          } else if (warningHeader.includes('40312')) {
            // User must be group admin
            showStatusOverlay({ message: t('group-management.error.user-not-admin'), type: 'error', dismissable: true })
          } else if (warningHeader.includes('40401') || warningHeader.includes('41001')) {
            // Group not found or tombstoned
            showStatusOverlay({
              message: t('group-management.error.group-not-active'),
              type: 'error',
              dismissable: true,
            })
          } else {
            showStatusOverlay({
              message: t('group-management.error.unexpected-error-edit'),
              type: 'error',
              dismissable: true,
            })
          }
        } else {
          showStatusOverlay({
            message: t('group-management.error.unexpected-error-edit'),
            type: 'error',
            dismissable: true,
          })
        }
      })
      .finally(() => {
        setIsSubmitting(false)
      })
  }

  return (
    <ZionForm data-testid="edit-tree-details-form" methods={methods} onSubmit={onSubmit}>
      <FormTextField
        name="name"
        data-testid="group-management.group-name.field"
        maxLength={MAX_NAME_CHARS}
        label={t('group-management.label.group-name')}
      />

      <FormMultilineTextField
        name="description"
        data-testid="group-management.group-description.field"
        maxLength={MAX_DESCRIPTION_CHARS}
        label={t('group-management.label.group-description')}
        autoComplete="off"
      />

      <Button
        type="submit"
        data-testid="edit-tree-details-submit-button"
        emphasis="medium"
        Icon={isSubmitting ? NoticeLoading : undefined}
        disabled={!isDirty || isSubmitting}
      >
        {t('common-ui:save.action')}
      </Button>
    </ZionForm>
  )
}

export function EditTreeDetailsSkeleton(): JSX.Element {
  return (
    <div>
      <Skeleton.TextInput />
      <Skeleton.TextInput />
      {/* eslint-disable-next-line test-selectors/button -- NA */}
      <Skeleton.Button />
    </div>
  )
}
